import React, { useEffect, useState, useContext, useRef } from 'react';
import { requestTradingData, closeMargin, cancelMargin } from 'api/socketService';
import SocketContext, { useSocket } from 'utils/hooks/useSocket';
import { useWalletData } from 'context/WalletDataContext';
import { useTradeUpdate } from 'context/TradeUpdateContext';
import SetTPSL from 'components/ui/SetTPSL';
import { useTournament } from 'context/TournamentContext';
import { deriveTournamentPhase } from 'utils/tournamentPhase';
import { CASH_TICKER } from 'utils/cash';

import {
  Wrap,
  TabStrip,
  TabBtn,
  ScrollWrap,
  Table,
  Th,
  Td,
  Tr,
  PairCell,
  PairIcon,
  SideLevCell,
  SideBadge,
  LevBadge,
  PnlCell,
  TPSLBox,
  ActionBtn,
  ConfirmCluster,
  ConfirmText,
  StatusBadge,
  Empty,
  WrapTPSL,
  WrapModal,
  ClosePopup,
} from './styled';

type Trade = {
  id: number;
  trading_pair: string;
  coin_amount: number;
  price: number;
  market_value: number;
  type: string;
  createdAt: string;
  updatedAt: string;
  transaction_type: string;
  leverage: number;
  status: string;
  is_settled: boolean;
  used_funds: number;
  liquidation_price: number;
  description: string;
  limit?: number;
  take_profit?: number;
  stop_loss?: number;
  profit?: number;
  profit_percentage?: number;
  break_even_price?: number;
  fee?: number;
};

const TradeHistoryMargin = (props: { tournamentDetails: any; }) => {
  const [activeTab, setActiveTab] = useState("tab1");
  const [tradingData, setTradingData] = useState<Trade[]>([]);
  const { tournamentDetails } = useSocket();
  const { walletData, update } = useWalletData();
  const { allCoinsValues, tradingUpdates } = useContext(SocketContext);
  const [isOpenTpSl, setIsOpenTpSl] = useState(false);
  const [takeProfit, setTakeProfit] = useState(undefined);
  const [stopLoss, setStopLoss] = useState(undefined);
  const { triggerTradeUpdate, tradeUpdated } = useTradeUpdate();
  const [currentTradeId, setCurrentTradeId] = useState(null);
  const [isOpenTransaction, setOpenTransaction] = useState<Record<number, boolean>>({});
  const { tournamentStatus, tournamentDetails: ctxTournamentDetails } = useTournament();
  const ctxRaw: any = ctxTournamentDetails?.data;
  const ctxAttributes = ctxRaw?.attributes ?? ctxRaw?.data ?? ctxRaw ?? {};
  const ctxEndAt: string | null | undefined = ctxAttributes?.end_at ?? ctxAttributes?.endDate;
  const phase = deriveTournamentPhase(tournamentStatus, ctxEndAt);
  const isTrading = phase === 'TRADING';

  useEffect(() => {
    if (!isTrading) {
      setActiveTab("tab3");
    }
  }, [isTrading]);

  const handleConfirm = (id: number) => {
    setOpenTransaction((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAccept = () => {
    setIsOpenTpSl(false);
    setTradingData((prev) =>
      prev.map((t) =>
        t.id === currentTradeId
          ? { ...t, take_profit: Number(takeProfit), stop_loss: Number(stopLoss) }
          : t
      )
    );
  };

  const handleCancelModal = () => setIsOpenTpSl(false);

  const updateTrading = (payload: any) => {
    let trades: Trade[] | undefined;
    if (Array.isArray(payload)) {
      trades = payload as Trade[];
    } else if (Array.isArray(payload?.data)) {
      trades = payload.data as Trade[];
    } else if (Array.isArray(payload?.data?.history)) {
      trades = payload.data.history as Trade[];
    }
    if (trades && trades.length > 0) {
      setTradingData(trades);
    } else {
      console.error('Error fetching trading data:', payload);
    }
  };

  const fetchTradingData = () => {
    const rawData: any = tournamentDetails?.data;
    const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
    const tournamentId = rawData?.id;
    let tradeType = attributes?.trade_type;
    if (typeof tradeType === 'string') {
      const lower = tradeType.toLowerCase();
      if (lower === 'margin') tradeType = 'Margin';
      else if (lower === 'spot') tradeType = 'Spot';
    }
    if (!tournamentId || !tradeType) return;
    requestTradingData(tournamentId, tradeType, updateTrading);
  };

  const didTradeUpdateMountRef = useRef(false);

  useEffect(() => {
    if (!didTradeUpdateMountRef.current) {
      didTradeUpdateMountRef.current = true;
      return;
    }
    if (tournamentDetails) fetchTradingData();
  }, [tradeUpdated]);

  const [currentWalletData, updateCurrentWalletData] = useState(null);

  if (walletData?.length) {
    if (currentWalletData?.length) {
      walletData.forEach((element: any, i: number) => {
        if (element?.coin_amount !== currentWalletData[i]?.coin_amount) {
          updateCurrentWalletData(walletData);
        }
      });
    } else {
      updateCurrentWalletData(walletData);
    }
  }

  useEffect(() => {
    if (currentWalletData && tournamentDetails) fetchTradingData();
  }, [currentWalletData, tournamentDetails]);

  useEffect(() => {
    if (tournamentDetails) fetchTradingData();
  }, [tournamentDetails]);

  const filterTradesByStatus = (statuses: string[], isSettled: boolean | null = null) => {
    return tradingData.filter((trade) => {
      if (isSettled !== null) {
        return statuses.includes(trade.status) && trade.is_settled === isSettled;
      }
      return statuses.includes(trade.status);
    });
  };

  const calculateProfit = (currentPrice: number, trade: Trade) => {
    const multiplier = trade.transaction_type === 'long' ? 1 : -1;
    const profit =
      (((currentPrice - trade.break_even_price) / trade.break_even_price) *
        (trade.used_funds - trade.fee) *
        trade.leverage *
        multiplier) -
      trade.fee;
    const profitPercent = (profit / trade.used_funds) * 100;
    return { profit, profitPercent };
  };

  useEffect(() => {
    if (tradingUpdates) {
      updateTrading(tradingUpdates);
      update();
    }
  }, [tradingUpdates]);

  const openTpSlEditor = (trade: Trade) => {
    if (!isTrading) return;
    setIsOpenTpSl(true);
    setTakeProfit(trade.take_profit ? Number(trade.take_profit) : undefined);
    setStopLoss(trade.stop_loss ? Number(trade.stop_loss) : undefined);
    setCurrentTradeId(trade.id);
  };

  const renderTPSL = (trade: Trade) => (
    <TPSLBox type="button" onClick={() => openTpSlEditor(trade)}>
      <span className="row">
        <span className="l tp">TP</span>
        <span className={trade.take_profit ? 'v' : 'empty'}>
          {trade.take_profit ?? '—'}
        </span>
      </span>
      <span className="row">
        <span className="l sl">SL</span>
        <span className={trade.stop_loss ? 'v' : 'empty'}>
          {trade.stop_loss ?? '—'}
        </span>
      </span>
    </TPSLBox>
  );

  const openTrades = filterTradesByStatus(['filled'], false);
  const pendingTrades = filterTradesByStatus(['pending'], false);
  const historyTrades = filterTradesByStatus(['filled', 'canceled']);

  return (
    <Wrap>
      <TabStrip>
        {isTrading && (
          <>
            <TabBtn className={activeTab === 'tab1' ? 'active' : ''} onClick={() => setActiveTab('tab1')}>
              Open ({openTrades.length})
            </TabBtn>
            <TabBtn className={activeTab === 'tab2' ? 'active' : ''} onClick={() => setActiveTab('tab2')}>
              Pending ({pendingTrades.length})
            </TabBtn>
          </>
        )}
        <TabBtn className={activeTab === 'tab3' ? 'active' : ''} onClick={() => setActiveTab('tab3')}>
          History
        </TabBtn>
      </TabStrip>

      {isOpenTpSl && (
        <WrapTPSL>
          <WrapModal>
            <ClosePopup onClick={() => setIsOpenTpSl(false)} />
            <h4>Edit your Take Profit / Stop Loss</h4>
            <SetTPSL
              takeProfit={takeProfit}
              setTakeProfit={setTakeProfit}
              stopLoss={stopLoss}
              setStopLoss={setStopLoss}
              onAccept={handleAccept}
              onCancel={handleCancelModal}
              tradeId={currentTradeId}
            />
          </WrapModal>
        </WrapTPSL>
      )}

      {activeTab === 'tab1' && (
        <ScrollWrap>
          {openTrades.length === 0 ? (
            <Empty>No open positions</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Pair</Th>
                  <Th>Side · Lev</Th>
                  <Th className="right">Size</Th>
                  <Th className="right">Entry</Th>
                  <Th className="right">Mark</Th>
                  <Th className="right">Liq.</Th>
                  <Th className="right">P&amp;L</Th>
                  <Th>TP/SL</Th>
                  <Th className="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {openTrades.map((trade) => {
                  const tradingPairKey = trade.trading_pair.split('/')[0].toLowerCase();
                  const currentPrice = allCoinsValues?.[tradingPairKey]?.last || 0;
                  const { profit, profitPercent } = calculateProfit(currentPrice, trade);
                  const coin = trade.trading_pair.split('/')[0];
                  const isConfirming = !!isOpenTransaction[trade.id];
                  return (
                    <Tr key={trade.id} className={isConfirming ? 'confirming' : ''}>
                      <Td>
                        <PairCell>
                          <PairIcon $src={`../icons/tradingPair/use/${coin}.svg`} />
                          {trade.trading_pair}
                        </PairCell>
                      </Td>
                      <Td>
                        <SideLevCell>
                          <SideBadge $side={trade.transaction_type === 'long' ? 'long' : 'short'}>
                            {trade.transaction_type === 'long' ? 'Long' : 'Short'}
                          </SideBadge>
                          <LevBadge>×{trade.leverage}</LevBadge>
                        </SideLevCell>
                      </Td>
                      <Td className="right">
                        {trade.coin_amount} {coin}
                      </Td>
                      <Td className="right muted">{trade.price.toFixed(2)}</Td>
                      <Td className="right muted">{currentPrice ? currentPrice.toFixed(2) : '—'}</Td>
                      <Td className="right down_state">{trade.liquidation_price.toFixed(2)}</Td>
                      <Td className="right">
                        <PnlCell $pos={profit > 0} $neg={profit < 0}>
                          {profit >= 0 ? '+' : ''}
                          {profit.toFixed(2)}
                          <small>
                            {profitPercent >= 0 ? '+' : ''}
                            {profitPercent.toFixed(2)}%
                          </small>
                        </PnlCell>
                      </Td>
                      <Td>{renderTPSL(trade)}</Td>
                      <Td className="right">
                        {isConfirming ? (
                          <ConfirmCluster>
                            <ConfirmText>Close?</ConfirmText>
                            <ActionBtn
                              type="button"
                              $tone="danger"
                              onClick={() => {
                                closeMargin(trade.id, (response: any) => {
                                  if (response.error) {
                                    console.error(response.error);
                                    alert(response.error);
                                  }
                                  update();
                                  triggerTradeUpdate();
                                  setOpenTransaction({});
                                });
                              }}
                            >
                              Yes
                            </ActionBtn>
                            <ActionBtn type="button" onClick={() => setOpenTransaction({})}>
                              No
                            </ActionBtn>
                          </ConfirmCluster>
                        ) : (
                          <ActionBtn type="button" $tone="danger" onClick={() => handleConfirm(trade.id)}>
                            Close
                          </ActionBtn>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </ScrollWrap>
      )}

      {activeTab === 'tab2' && (
        <ScrollWrap>
          {pendingTrades.length === 0 ? (
            <Empty>No pending orders</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Pair</Th>
                  <Th>Side · Lev</Th>
                  <Th className="right">Size</Th>
                  <Th className="right">Limit</Th>
                  <Th className="right">Used Funds</Th>
                  <Th>TP/SL</Th>
                  <Th className="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {pendingTrades.map((trade) => {
                  const coin = trade.trading_pair.split('/')[0];
                  const isConfirming = !!isOpenTransaction[trade.id];
                  return (
                    <Tr key={trade.id} className={isConfirming ? 'confirming' : ''}>
                      <Td>
                        <PairCell>
                          <PairIcon $src={`../icons/tradingPair/use/${coin}.svg`} />
                          {trade.trading_pair}
                        </PairCell>
                      </Td>
                      <Td>
                        <SideLevCell>
                          <SideBadge $side={trade.transaction_type === 'long' ? 'long' : 'short'}>
                            {trade.transaction_type === 'long' ? 'Long' : 'Short'}
                          </SideBadge>
                          <LevBadge>×{trade.leverage}</LevBadge>
                        </SideLevCell>
                      </Td>
                      <Td className="right">
                        {trade.coin_amount} {coin}
                      </Td>
                      <Td className="right">{trade.limit} {CASH_TICKER}</Td>
                      <Td className="right muted">{trade.used_funds} {CASH_TICKER}</Td>
                      <Td>{renderTPSL(trade)}</Td>
                      <Td className="right">
                        {isConfirming ? (
                          <ConfirmCluster>
                            <ConfirmText>Cancel?</ConfirmText>
                            <ActionBtn
                              type="button"
                              $tone="danger"
                              onClick={() => {
                                cancelMargin(trade.id, (response: any) => {
                                  if (response && response.error) {
                                    if (response.error === 'Margin not found') {
                                      console.warn(response.error);
                                      fetchTradingData();
                                      update();
                                      setOpenTransaction({});
                                      return;
                                    }
                                    console.error(response.error);
                                    alert(response.error);
                                    fetchTradingData();
                                    update();
                                    setOpenTransaction({});
                                    return;
                                  }
                                  update();
                                  triggerTradeUpdate();
                                  setOpenTransaction({});
                                });
                              }}
                            >
                              Yes
                            </ActionBtn>
                            <ActionBtn type="button" onClick={() => setOpenTransaction({})}>
                              No
                            </ActionBtn>
                          </ConfirmCluster>
                        ) : (
                          <ActionBtn type="button" $tone="danger" onClick={() => handleConfirm(trade.id)}>
                            Cancel
                          </ActionBtn>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </ScrollWrap>
      )}

      {activeTab === 'tab3' && (
        <ScrollWrap>
          {historyTrades.length === 0 ? (
            <Empty>No history</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Pair</Th>
                  <Th>Type</Th>
                  <Th>Lev</Th>
                  <Th className="right">Used Funds</Th>
                  <Th className="right">Amount</Th>
                  <Th className="right">Price</Th>
                  <Th className="right">Profit</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {historyTrades.map((trade) => {
                  const historyPrice =
                    trade.status === 'canceled' && typeof trade.limit === 'number'
                      ? trade.limit
                      : trade.price;
                  const coin = trade.trading_pair.split('/')[0];
                  const profitVal = trade.profit;
                  return (
                    <Tr key={trade.id}>
                      <Td>
                        <PairCell>
                          <PairIcon $src={`../icons/tradingPair/use/${coin}.svg`} />
                          {trade.trading_pair}
                        </PairCell>
                      </Td>
                      <Td className="muted">{trade.description}</Td>
                      <Td>
                        <LevBadge>×{trade.leverage}</LevBadge>
                      </Td>
                      <Td className="right">{trade.used_funds} {CASH_TICKER}</Td>
                      <Td className="right">
                        {trade.coin_amount} {coin}
                      </Td>
                      <Td className="right">{historyPrice.toFixed(2)} {CASH_TICKER}</Td>
                      <Td className="right">
                        {profitVal !== null && profitVal !== undefined ? (
                          <PnlCell $pos={profitVal > 0} $neg={profitVal < 0}>
                            {profitVal >= 0 ? '+' : ''}
                            {profitVal} {CASH_TICKER}
                          </PnlCell>
                        ) : (
                          <span style={{ color: '#7A7E8C' }}>—</span>
                        )}
                      </Td>
                      <Td>
                        <StatusBadge $kind={trade.status === 'filled' ? 'filled' : 'canceled'}>
                          {trade.status.charAt(0).toUpperCase() + trade.status.slice(1)}
                        </StatusBadge>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </ScrollWrap>
      )}
    </Wrap>
  );
};

export default TradeHistoryMargin;
